import { TiMinus, TiPlus } from 'react-icons/ti';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { GoDotFill } from 'react-icons/go';
import { CheckboxInput } from '@/features/editor/widgets/custom-widget/inputs/shared/checkbox-input';
import {
  AdSearchContext,
  AdSearchState,
} from '@/features/editor/widgets/shared/modals/audience/facebook-audience/ad-search/context';
import {
  FacebookAd,
  FacebookAdSet,
  FacebookTargeting,
} from '@/components/query-builder/models';
import {
  FBAd,
  FBAdSet,
  FBCampaign,
} from '@/features/editor/widgets/shared/modals/audience/facebook-audience/ad-search/models';

export function Details({
  defaultValue,
  name,
  onChange,
  status,
  onExpand,
}: {
  defaultValue: boolean;
  onChange: (value) => void;
  status: string;
  name: string;
  onExpand?: () => void;
}) {
  const { state } = useContext(AdSearchContext);

  const splitIdx = name?.toLowerCase().indexOf(state?.query?.toLowerCase());
  const start = name.slice(0, splitIdx);
  const middle = name.slice(splitIdx, splitIdx + state.query.length);
  const end = name.slice(splitIdx + state.query.length);

  return (
    <FlexRow>
      <CheckboxInput
        defaultValue={defaultValue}
        value={defaultValue}
        onChange={onChange}
        zIndex={32}
      />
      <GoDotFill
        style={{ marginRight: `2rem` }}
        size={20}
        color={status === `PAUSED` ? `orange` : `green`}
      />
      <Name onClick={onExpand}>
        {splitIdx < 0 ? (
          name
        ) : (
          <span>
            {start}
            <mark>{middle}</mark>
            {end}
          </span>
        )}
      </Name>
    </FlexRow>
  );
}

const FlexRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const Name = styled.span`
  flex: 1;
  min-width: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: start;
  white-space: nowrap;
  :hover {
    cursor: pointer;
  }
  span {
    user-select: text;
  }
  user-select: text;
  color: #576470;
`;

export function ExpandBtn({
  isCollapsed,
  onClick,
}: {
  onClick: () => void;
  isCollapsed: boolean;
}) {
  return (
    <PlusBtn type="button" onClick={onClick}>
      {isCollapsed ? (
        <TiPlus color="#d8d8d8" size={20} />
      ) : (
        <TiMinus color="#d8d8d8" size={20} />
      )}
    </PlusBtn>
  );
}

const PlusBtn = styled.button`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 1rem;

  :hover {
    opacity: 0.7;
    cursor: pointer;
  }

  :active {
    cursor: default;
  }
`;

const LoadMoreBtnContainer = styled.button`
  width: 100%;
  margin: 0.5rem auto ${(p: any) => p?.mb || `0`} auto;
  text-align: center;
  user-select: none;
  font-family: 'JetBrains Mono', serif;
  :hover {
    opacity: 0.7;
    cursor: pointer;
  }
  :active {
    opacity: 1;
    cursor: default;
  }
`;

type LoadMoreBtnProps = {
  onClick: () => void;
  loading: boolean;
  name?: string;
  mb?: string;
};

export function LoadMoreBtn({ onClick, loading, name, mb }: LoadMoreBtnProps) {
  return (
    <LoadMoreBtnContainer
      mb={mb}
      disabled={loading}
      type="button"
      onClick={onClick}
    >
      {loading ? `.........` : `load more ${name || ``}`}
    </LoadMoreBtnContainer>
  );
}

export function clone(x: any): any {
  return JSON.parse(JSON.stringify(x));
}

export function adSetHasSelectedChild(
  draft: AdSearchState,
  campaignIdx: number,
  adsetIdx: number,
) {
  return draft.campaigns?.[campaignIdx]?.adSets?.[adsetIdx]?.ads?.find(
    (x) => x.isSelected,
  );
}

export function campaignHasSelectedChild(
  draft: AdSearchState,
  campaignIdx: number,
) {
  const { adSets } = draft.campaigns[campaignIdx];
  const hasSelectedGrandSon = adSets
    ?.flatMap((x) => x?.ads?.map((x) => x.isSelected))
    ?.find((x) => x);
  const hasSelectedSon = adSets?.map((x) => x.isSelected).find((x) => x);
  return hasSelectedSon || hasSelectedGrandSon;
}

const isAdSelected = (ad: FBAd) => !!ad?.isSelected;

const adSetHasSelectedAd = (adSet: FBAdSet) => !!adSet?.ads?.some(isAdSelected);

const adSetIsSelectedOrHasSelectedAd = (adSet: FBAdSet) =>
  !!adSet?.isSelected || adSetHasSelectedAd(adSet);

export const isCampaignTargetedAsWhole = (campaign: FBCampaign) =>
  !!campaign?.isSelected &&
  !campaign.adSets?.some(adSetIsSelectedOrHasSelectedAd);

export const isAdSetTargetedAsWhole = (adSet: FBAdSet) =>
  !!adSet?.isSelected && !adSetHasSelectedAd(adSet);

export function getFBQuery(targeting: FacebookTargeting) {
  return {
    value: ``,
    ads: targeting.ads,
    adsets: targeting.adsets,
    campaigns: targeting.campaigns,
  };
}

export const targetingFromState = (
  targeting: React.MutableRefObject<FacebookTargeting>,
  state: AdSearchState,
) => {
  targeting.current = clone(targeting.current);
  const selectedAds =
    state.campaigns
      ?.flatMap((campaign) =>
        campaign.adSets?.flatMap((adset) =>
          adset?.ads
            ?.filter((ad) => ad.isSelected)
            ?.map(
              (ad) =>
                ({
                  id: ad.id,
                  name: ad.name,
                  adSet_id: adset.id,
                  adsetName: adset.name,
                  campaign_id: campaign.id,
                  campaignName: campaign.name,
                } as FacebookAd),
            ),
        ),
      )
      ?.filter((x) => !!x) ?? [];

  const selectedAdSets =
    state.campaigns
      ?.flatMap((campaign) =>
        campaign.adSets?.filter(isAdSetTargetedAsWhole)?.map(
          (s) =>
            ({
              name: s.name,
              id: s.id,
              campaign_id: campaign.id,
              campaignName: campaign.name,
            } as FacebookAdSet),
        ),
      )
      ?.filter((x) => !!x) ?? [];

  const selectedCampaigns =
    state.campaigns
      ?.filter(isCampaignTargetedAsWhole)
      ?.map((x) => ({
        name: x.name,
        id: x.id,
      }))
      ?.filter((x) => !!x) ?? [];

  targeting.current.campaigns = clone(selectedCampaigns);
  targeting.current.adsets = clone(selectedAdSets);
  targeting.current.ads = clone(selectedAds);

  return targeting.current;
};
